import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Monitor, Clock, Copy } from "lucide-react";
import { toast } from "sonner";

const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];

function ScheduleRow({ sc, experiences, onDelete }) {
  const expName = experiences.find((e) => e.experience_id === sc.experience_id)?.name || "—";
  const dayLabel = (sc.days_of_week || []).map((d) => DAYS.find((x) => x[1] === d)?.[0]).join(", ");
  return (
    <div className="flex items-center justify-between gap-2 text-sm py-2 border-t border-[#EFEBE3]" data-testid={`schedule-row-${sc.schedule_id}`}>
      <div>
        <span className="font-medium text-[#2A2626]">{dayLabel}</span>
        <span className="text-[#5C5856]"> · {sc.start_time}–{sc.end_time} · </span>
        <span className="text-[#2A2626]">{expName}</span>
        {!sc.enabled && <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-[#F0EBE1] text-[#5C5856]">Disabled</span>}
      </div>
      <Button variant="ghost" size="sm" onClick={() => onDelete(sc.schedule_id)} data-testid={`schedule-delete-${sc.schedule_id}`}><Trash2 className="w-4 h-4 text-[#B23A48]" /></Button>
    </div>
  );
}

function DeviceCard({ device, experiences, slug, reload }) {
  const [schedules, setSchedules] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ experience_id: experiences[0]?.experience_id || "", days_of_week: [], start_time: "17:00", end_time: "21:00", enabled: true });

  const loadSchedules = async () => {
    try { const { data } = await api.get(`/schedules?device_id=${device.device_id}`); setSchedules(data); } catch { /* ignore */ }
  };
  useEffect(() => { loadSchedules(); /* eslint-disable-next-line */ }, [device.device_id]);

  const toggleDay = (d) => setForm((f) => ({ ...f, days_of_week: f.days_of_week.includes(d) ? f.days_of_week.filter((x) => x !== d) : [...f.days_of_week, d] }));

  const saveSchedule = async () => {
    try {
      await api.post("/schedules", { device_id: device.device_id, ...form });
      toast.success("Schedule added");
      setAdding(false); setForm({ experience_id: experiences[0]?.experience_id || "", days_of_week: [], start_time: "17:00", end_time: "21:00", enabled: true });
      loadSchedules();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };
  const deleteSchedule = async (id) => { try { await api.delete(`/schedules/${id}`); loadSchedules(); } catch { /* ignore */ } };

  const setDefault = async (expId) => { try { await api.put(`/devices/${device.device_id}`, { default_experience_id: expId }); toast.success("Default experience updated"); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleEnabled = async (v) => { try { await api.put(`/devices/${device.device_id}`, { enabled: v }); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const rename = async () => { const name = window.prompt("Rename device", device.name); if (!name) return; try { await api.put(`/devices/${device.device_id}`, { name }); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const remove = async () => { if (!window.confirm("Remove this device and its schedules?")) return; try { await api.delete(`/devices/${device.device_id}`); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const copyPairUrl = () => {
    const url = `${window.location.origin}/kiosk/${slug}?device=${device.device_token}`;
    navigator.clipboard?.writeText(url); toast.success("Pairing link copied — open it on the kiosk device");
  };

  return (
    <Card className="p-5 space-y-3" data-testid={`device-card-${device.device_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Monitor className="w-4 h-4 text-[#2A2626]" />
            <span className="font-semibold text-[#2A2626]" data-testid={`device-name-${device.device_id}`}>{device.name}</span>
            {!device.enabled && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0EBE1] text-[#5C5856]">Disabled</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2"><Switch checked={device.enabled !== false} onCheckedChange={toggleEnabled} data-testid={`device-enabled-${device.device_id}`} /><Label className="text-xs text-[#5C5856]">Enabled</Label></div>
          <Button variant="outline" size="sm" className="rounded-full h-8" onClick={copyPairUrl} data-testid={`device-pair-${device.device_id}`}><Copy className="w-3.5 h-3.5 mr-1" /> Pairing link</Button>
          <Button variant="ghost" size="sm" onClick={rename} data-testid={`device-rename-${device.device_id}`}>Rename</Button>
          <Button variant="ghost" size="sm" onClick={remove} data-testid={`device-delete-${device.device_id}`}><Trash2 className="w-4 h-4 text-[#B23A48]" /></Button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Label className="text-sm text-[#5C5856] w-32">Default experience</Label>
        <Select value={device.default_experience_id || ""} onValueChange={setDefault}>
          <SelectTrigger className="h-9 max-w-[280px]" data-testid={`device-default-exp-${device.device_id}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{experiences.map((e) => <SelectItem key={e.experience_id} value={e.experience_id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="pt-2">
        <div className="flex items-center gap-2 text-sm font-medium text-[#2A2626]"><Clock className="w-4 h-4" /> Schedules</div>
        {schedules.map((sc) => <ScheduleRow key={sc.schedule_id} sc={sc} experiences={experiences} onDelete={deleteSchedule} />)}
        {schedules.length === 0 && <p className="text-xs italic text-[#5C5856] mt-1">No schedules — this device always shows its default experience.</p>}

        {adding ? (
          <div className="mt-3 p-3 rounded-xl bg-[#F7F5F1] space-y-3" data-testid={`schedule-form-${device.device_id}`}>
            <div className="flex flex-wrap gap-2">
              {DAYS.map(([lbl, d]) => (
                <button key={d} type="button" onClick={() => toggleDay(d)} className={`px-2.5 h-8 rounded-full text-xs font-medium ${form.days_of_week.includes(d) ? "bg-[#2A2626] text-white" : "bg-white border border-[#E5E2DC] text-[#5C5856]"}`} data-testid={`schedule-day-${device.device_id}-${d}`}>{lbl}</button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div><Label className="text-xs">Start</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="h-9 w-32" data-testid={`schedule-start-${device.device_id}`} /></div>
              <div><Label className="text-xs">End</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="h-9 w-32" data-testid={`schedule-end-${device.device_id}`} /></div>
              <div className="flex-1 min-w-[180px]"><Label className="text-xs">Experience</Label>
                <Select value={form.experience_id} onValueChange={(v) => setForm({ ...form, experience_id: v })}>
                  <SelectTrigger className="h-9" data-testid={`schedule-exp-${device.device_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{experiences.map((e) => <SelectItem key={e.experience_id} value={e.experience_id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={saveSchedule} className="bg-[#2A2626] text-white rounded-full h-9" data-testid={`schedule-save-${device.device_id}`}>Add schedule</Button>
              <Button variant="ghost" onClick={() => setAdding(false)} className="h-9">Cancel</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" className="mt-3 rounded-full h-9 border-dashed" onClick={() => setAdding(true)} data-testid={`schedule-add-${device.device_id}`}><Plus className="w-4 h-4 mr-1" /> Add schedule</Button>
        )}
      </div>
    </Card>
  );
}

export function DevicesManager({ experiences, slug }) {
  const [devices, setDevices] = useState([]);
  const reload = async () => { try { const { data } = await api.get("/devices"); setDevices(data); } catch { /* ignore */ } };
  useEffect(() => { reload(); }, []);

  const addDevice = async () => {
    const name = window.prompt("Name this device", "Lobby iPad");
    if (!name) return;
    try { await api.post("/devices", { name }); toast.success("Device registered"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create"); }
  };

  return (
    <Card className="p-6" data-testid="devices-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-base font-semibold text-[#2A2626] flex items-center gap-2"><Monitor className="w-4 h-4" /> Devices &amp; scheduling</h2>
          <p className="text-sm text-[#5C5856] mt-1">Assign experiences to specific kiosks and switch them automatically by day &amp; time.</p>
        </div>
        <Button onClick={addDevice} className="bg-[#2A2626] hover:bg-[#1A1717] text-white rounded-full" data-testid="device-create"><Plus className="w-4 h-4 mr-1.5" /> Register device</Button>
      </div>
      <div className="space-y-4" data-testid="devices-list">
        {devices.map((d) => <DeviceCard key={d.device_id} device={d} experiences={experiences} slug={slug} reload={reload} />)}
        {devices.length === 0 && <p className="text-sm italic text-[#5C5856]">No devices yet. Register your first kiosk to assign it an experience.</p>}
      </div>
    </Card>
  );
}
